import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import avatarGrey from 'assets/images/defaultAvatar-grey.png';
import avatarWhite from 'assets/images/defaultAvatar-white.svg';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import calendar from 'assets/icons/calendar-check-02.svg';
import { getUserRoleFromToken } from 'api';
import EditMatchResultModal from 'components/EditResultModal';
import { Edit } from '@mui/icons-material';
import { ErrorToast, SuccessToast } from 'utils/Toasts';

const TournamentPlayerCard = ({
  pRez,
  player1_id,
  player2_id,
  player1_name,
  player2_name,
  player1_image,
  player2_image,
  winner,
  isChallenger,
  teamId,
  match_id,
  match_details,
  fetchMainTable,
  stage, // Add stage prop to determine playoffs or losers
}) => {
  const theme = useTheme();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const rez = pRez ? pRez : '';
  const isWinner = winner === parseInt(teamId);
  const [showTooltip, setShowTooltip] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const isAdmin = getUserRoleFromToken() === 'admin' ? true : false;

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      classes={{ popper: className }}
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.base.white,
      color: theme.palette.darkGrey[700],
      boxShadow:
        '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
      padding: '8px 12px',
      borderRadius: '8px',
    },
  }));

  const openEditModal = (match) => {
    setSelectedMatch(match);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedMatch(null);
    setIsModalOpen(false);
  };

  const splitMatchResult = (result, isChallenger) => {
    const sets = result.split(' ');
    const scores = sets.map((set) => set.split(':'));

    const playerScores = scores.map((score) => {
      const playerScore = isChallenger ? score[0] : score[1];
      return playerScore;
    });

    return playerScores;
  };

  const updatedRez = (rez) => {
    const parts = rez.split(' ');
    const regex = /\((\d+)\)/g;
    const superscriptParts = parts.map((part) =>
      part.replace(regex, '<sup>$1</sup>'),
    );
    const superscriptRezultatas = superscriptParts.join(' ');
    return superscriptRezultatas;
  };

  const playerScores = splitMatchResult(rez, isChallenger);

  return (
    <>
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <EditMatchResultModal
        open={isModalOpen}
        onClose={closeEditModal}
        match={selectedMatch}
        fetchMainTable={fetchMainTable}
        successToastRef={successToastRef}
        errorToastRef={errorToastRef}
        openedFrom={stage || "playoffs"}
      />
      <Box
        sx={{
          width: '304px',
          height: 'auto',
          display: 'flex',
          padding: '4px 10px',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: isWinner ? '8px' : '0px',
          backgroundColor: isWinner
            ? theme.palette.darkGrey[100]
            : 'transparent',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'flex-start',
            gap: '4px',
            width: '100%',
          }}
        >
          {/* First Player */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component={'img'}
              src={
                player1_image
                  ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${player1_image}`
                  : isWinner
                  ? avatarWhite
                  : avatarGrey
              }
              alt="avatar"
              sx={{
                width: '32px',
                height: '32px',
                objectFit: 'cover',
                borderRadius: '50%',
                marginRight: '8px',
              }}
            />
            <Box
              component={'a'}
              sx={{
                display: 'flex',
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                  textDecorationColor: theme.palette.darkGrey[700],
                },
              }}
              href={`${window.location.origin}/info/${player1_id}`}
            >
              <Typography
                variant={'body4'}
                fontWeight={600}
                textAlign={'left'}
                color={theme.palette.darkGrey[700]}
              >
                {player1_name}
              </Typography>
            </Box>
          </Box>
          {/* Second Player */}
          {player2_id && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                component={'img'}
                src={
                  player2_image
                    ? `${process.env.REACT_APP_BACKEND_URL_BASE}/public/images/${player2_image}`
                    : isWinner
                    ? avatarWhite
                    : avatarGrey
                }
                alt="avatar"
                sx={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                  marginRight: '8px',
                }}
              />
              <Box
                component={'a'}
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    textDecorationColor: theme.palette.darkGrey[700],
                  },
                }}
                href={`${window.location.origin}/info/${player2_id}`}
              >
                <Typography
                  variant={'body4'}
                  fontWeight={600}
                  color={theme.palette.darkGrey[700]}
                >
                  {player2_name}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: 'auto',
          }}
        >
          {match_details && !rez ? (
            <LightTooltip
              title={match_details}
              placement={'top'}
              open={showTooltip[`${player1_id}`] || false}
              onOpen={() =>
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${player1_id}`]: true,
                }))
              }
              onClose={() =>
                setShowTooltip((prevState) => ({
                  ...prevState,
                  [`${player1_id}`]: false,
                }))
              }
            >
              <Box
                component={'img'}
                src={calendar}
                marginLeft={'8px'}
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  setShowTooltip((prevState) => ({
                    ...prevState,
                    [`${player1_id}`]: !prevState[`${player1_id}`],
                  }))
                }
                alt="calendar icon"
              />
            </LightTooltip>
          ) : (
            playerScores
              .filter((score) => score.trim() !== '')
              .map((score, index) => {
                return (
                  <Typography
                    key={index}
                    variant={'body4'}
                    fontWeight={600}
                    color={
                      isWinner
                        ? theme.palette.darkGrey[700]
                        : theme.palette.darkGrey[600]
                    }
                    dangerouslySetInnerHTML={{
                      __html: updatedRez(score),
                    }}
                    sx={{
                      '&:not(:last-child)': {
                        marginRight: '20px',
                      },
                    }}
                  />
                );
              })
          )}
        </Box>
        {isAdmin && match_id && (
          <Edit
            sx={{
              ml: '4px',
              fontSize: '16px',
              color: theme.palette.grey[500],
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
            onClick={() => openEditModal(match_id)}
          />
        )}
      </Box>
    </>
  );
};

export default TournamentPlayerCard;
